import React, { useState, useEffect } from 'react';
import { apiService, FAQ } from '../services/api';
import { Plus, Edit, Trash2, HelpCircle, Layers } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

const FAQs: React.FC = () => {
  const { showToast } = useToast();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const data = await apiService.getFaqs();
      setFaqs(data || []);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);

    const faqData = {
      question: fd.get('question') as string,
      answer: fd.get('answer') as string,
      faq_type: fd.get('faq_type') as string,
      category: fd.get('category') as string,
      order: parseInt(fd.get('order') as string) || 0,
      is_active: fd.get('is_active') === 'on' || fd.get('is_active') === 'true',
    };

    try {
      if (editingFaq) {
        await apiService.updateFaq(editingFaq.id, faqData);
      } else {
        await apiService.createFaq(faqData);
      }

      setShowForm(false);
      setEditingFaq(null);
      showToast('FAQ saved successfully!', 'success');
      fetchFaqs();
    } catch (error) {
      console.error('Failed to save FAQ:', error);
      showToast('Error saving FAQ.', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to remove this question?')) {
      try {
        await apiService.deleteFaq(id);
        showToast('FAQ removed successfully!', 'success');
        fetchFaqs();
      } catch (error) {
        console.error('Failed to delete FAQ:', error);
        showToast('Failed to delete FAQ.', 'error');
      }
    }
  };

  const categories = Array.from(new Set(faqs.map(f => f.faq_type || 'general'))).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">FAQs</h1>
          <p className="text-gray-600">Manage frequently asked questions and categories</p>
        </div>
        <button
          onClick={() => { setEditingFaq(null); setShowForm(true); }}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Question
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900">
                {editingFaq ? 'Edit FAQ' : 'New FAQ'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                <input name="question" defaultValue={editingFaq?.question} required className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500/20 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                <textarea name="answer" defaultValue={editingFaq?.answer} required rows={4} className="w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500/20 outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">FAQ Type</label>
                  <select name="faq_type" defaultValue={editingFaq?.faq_type || 'general'} className="w-full border border-gray-200 rounded-lg px-4 py-2">
                    <option value="general">General</option>
                    <option value="service">Service-Specific</option>
                    <option value="product">Product-Specific</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category (Optional)</label>
                  <input name="category" defaultValue={editingFaq?.category} placeholder="e.g. Pricing" className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                <input type="number" name="order" defaultValue={editingFaq?.order || 0} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <input type="checkbox" name="is_active" id="faq_active" defaultChecked={editingFaq?.is_active !== false} className="h-5 w-5 text-orange-600 rounded" />
                <label htmlFor="faq_active" className="text-sm font-medium text-gray-700">Show publicly</label>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-50">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 font-medium">Cancel</button>
                <button type="submit" className="px-6 py-2 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition">
                  {editingFaq ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {categories.map(cat => (
        <div key={cat} className="mb-10">
          <div className="flex items-center gap-2 mb-4 border-b pb-2">
            <Layers className="h-4 w-4 text-orange-600" />
            <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide">{cat}</h2>
          </div>

          <div className="space-y-4">
            {faqs.filter(f => (f.faq_type || 'general') === cat).sort((a, b) => a.order - b.order).map(faq => (
              <div key={faq.id} className="bg-white rounded-lg border border-gray-100 p-5 hover:shadow-sm transition group">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold bg-gray-100 text-gray-500 px-1.5 rounded">#{faq.order}</span>
                      <h3 className="font-bold text-gray-900">{faq.question}</h3>
                      {!faq.is_active && <span className="text-[10px] text-red-400 font-bold uppercase">(Hidden)</span>}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={() => { setEditingFaq(faq); setShowForm(true); }} className="p-2 text-gray-400 hover:text-orange-600"><Edit className="h-4 w-4" /></button>
                    <button onClick={() => handleDelete(faq.id)} className="p-2 text-gray-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {faqs.length === 0 && (
        <div className="text-center py-24 bg-gray-50 rounded-xl border border-gray-100">
          <HelpCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No FAQs found. Click "Add Question" to start.</p>
        </div>
      )}
    </div>
  );
};

export default FAQs;